'use strict';

var defaults = require('../defaults/Defaults');
var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeEvent = require('./KayleeEvent');
var KayleeEvents = require('./KayleeEvents');
var KayleeLogs = require('./KayleeLogs');
var KayleeIsRole = require('./KayleeIsRole');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeController');
var seedrandom = require('seedrandom');
var sutl = require('../sutl/Sutl');

var KayleeController = {
    addUpdatesToCreateController: addUpdatesToCreateController,
    addUpdatesToPairController: addUpdatesToPairController,
    addUpdatesToUnpairController: addUpdatesToUnpairController,
    factoryResetController: factoryResetController,
    fromJson: fromJson,
    getController: getController,
    getControllerBySerialNumber: getControllerBySerialNumber,
    pairUsingCode: pairUsingCode,
    requestPairingCode: requestPairingCode,
};

var rng = null;

function addUpdatesToCreateController(update, controllerId, serialNumber, factoryInstallMode) {
    if (typeof factoryInstallMode === 'undefined') factoryInstallMode = true;

    update['controllers/' + controllerId] = {
        serialNumber: serialNumber,
        settings: {
            factoryInstallMode: factoryInstallMode,
        },
    };
    update['index/controllersBySerialNumber/' + serialNumber] = controllerId;

    return KayleeEvent.addUpdatesForControllerCreatedEvent(update, controllerId,
        serialNumber);
}

function addUpdatesToPairController(update, companyId, siteId, roomId,
    controllerId, actorUserId) {

    return KayleeUtil.loadControllerData({ controllerId: controllerId })
    .then(function(obj) {
        update['controllers/' + controllerId + '/companyId'] = companyId;
        update['controllers/' + controllerId + '/siteId'] = siteId;
        update['controllers/' + controllerId + '/roomId'] = roomId;
        update['index/controllersByRoom/' + roomId + '/' + controllerId] = true;
        update['rooms/' + roomId + '/uiPreview'] = null;

        if (obj.controller.hasOwnProperty('pairing')) {
            update['controllers/' + controllerId + '/pairing'] = null;
            update['index/controllersByPairingCode/' + obj.controller.pairing.code] = null;
        }
    })
    .then(function() {
        return KayleeEvent.addUpdatesForControllerPairedEvent(
            update, controllerId, roomId, actorUserId);
    });
}

function addUpdatesToSetPairingCode(update, controllerId, code) {
    var timestamp = Date.now();
    var expires = timestamp + 60 * 60 * 1000;

    update['controllers/' + controllerId + '/pairing'] = {
        code: code,
        time: timestamp,
        expires: expires,
    };

    return Promise.resolve(update);
}

function addUpdatesToUnpairController(update, roomId, controllerId, parentEventId, deleteUiPreview) {
    if (typeof deleteUiPreview === 'undefined') deleteUiPreview = true;

    update['controllers/' + controllerId + '/companyId'] = null;
    update['controllers/' + controllerId + '/siteId'] = null;
    update['controllers/' + controllerId + '/roomId'] = null;
    update['index/controllersByRoom/' + roomId + '/' + controllerId] = null;
    if (deleteUiPreview) update['rooms/' + roomId + '/uiPreview'] = null;

    return KayleeEvent.addUpdatesForControllerUnpairedEvent(
        update, controllerId, parentEventId);
}

function factoryResetController(serialNumber) {
    return getControllerBySerialNumber(serialNumber)
    .then(function(controller) { return controller.$loaded(); })
    .then(function(controller) {
        if (controller.hasOwnProperty('roomId')) {
            return controller.$unpair()
            .then(function() { return controller; });
        }

        return controller;
    })
    .then(function(controller) {
        return Promise.all([
            KayleeBase.app.database()
                .ref('auth/secrets/' + serialNumber)
                .set(null),
            KayleeBase.app.database()
                .ref('controllers/' + controller.$id + '/settings/factoryInstallMode')
                .set(true),
        ]);
    });
}

function fromJson(data) {
    var reqProps = [ '$id' ];
    return FirebaseObject.$fromJson(data, 'Controller', reqProps, reqProps,
        function(data) { return getController(data.$id); }
    );
}

function generatePairingCode() {
    var code = '';

    for (var i = 0; i < 6; i++) {
        code += getRandomInt(0, 9).toString();
    }

    return code;
}

function getController(controllerId) {
    var key = 'Controller: ' + controllerId;
    var controller = KayleeCache.get(key);

    if (controller === null) {
        controller = new Controller(controllerId);
        KayleeCache.put(key, controller);
    }

    return controller;
}

// Get the Controller object for the controller that has the specified serial
// number. Returns a promise that resolves to the Controller object (which may
// or may not be $loaded() yet).

function getControllerBySerialNumber(serialNumber) {
    log.debug('getControllerBySerialNumber: ' + serialNumber);
    var err;

    if (!KayleeConnectionState.isWorkingOnline()) {
        var cache = KayleeCache.getCache();

        for (var key in cache) {
            if (!cache.hasOwnProperty(key)) continue;
            var item = cache[key];

            if (item.$nodeType === 'controller' &&
                item.serialNumber === serialNumber) {
                return item.$loaded();
            }
        }

        err = new Error('did not find controller with serialNumber ' +
            serialNumber + ' in cache');
        log.warning(err.message);
        return Promise.reject(err);
    }

    return new Promise(function(resolve, reject) {
        KayleeBase.app.database()
        .ref('index/controllersBySerialNumber/' + serialNumber)
        .once('value', function(snap) {
            if (snap.exists()) {
                var controllerId = snap.val();
                log.debug('found controller id ' + controllerId +
                        ' for serial number ' + serialNumber);
                var controller = getController(controllerId);
                controller.$loaded()
                .then(resolve);
            } else {
                err = new Error('did not find controller serial number ' +
                    serialNumber + ' in index');
                log.warning(err.message);
                reject(err);
            }
        }, function(err) {
            err = new Error('failed looking up controller by serial number ' +
                serialNumber + ', error: ' + err);
            log.error(err.message);
            reject(err);
        });
    });
}

function getRandomInt(min, max) {
    if (rng === null) {
        rng = seedrandom();
    }

    return Math.floor(rng() * (max - min)) + min;
}

function pairUsingCode(userId, roomId, pairingCode) {
    var user;
    var room;
    var controller;

    return Promise.all([
        KayleeUtil.loadUserData({ userId: userId }, false),
        KayleeUtil.loadRoomData({ roomId: roomId }, false),
    ])
    .then(function(allResults) {
        user = allResults[0].user;
        room = allResults[1].room;

        if (user === null) {
            return Promise.reject('invalidUser');
        }

        if (room === null) {
            return Promise.reject('invalidRoom');
        }

        user.$id = userId;
        room.$id = roomId;
        room.$nodeType = 'room';

        if (!KayleeIsRole.isRole(user, room, 'doer')) {
            return Promise.reject(new Error('permissionDenied'));
        }

        return KayleeBase.app.database()
            .ref('index/controllersByPairingCode').once('value');
    })
    .then(function(snap) {
        var codeIndex = snap.exists() ? snap.val() : {};
        var codes = Object.keys(codeIndex);

        if (!codes.includes(pairingCode)) {
            return Promise.reject(new Error('invalidPairingCode'));
        }

        return KayleeUtil.loadControllerData({
            controllerId: codeIndex[pairingCode]
        }, false);
    })
    .then(function(obj) {
        controller = obj.controller;

        if (controller === null) {
            return Promise.reject(new Error('invalidController'));
        }

        controller.$id = obj.controllerId;
        var controllerCode = sutl.getNode(controller, [ 'pairing', 'code' ]);
        var expires = sutl.getNode(controller, [ 'pairing', 'expires' ]);

        if (controllerCode !== pairingCode) {
            return Promise.reject(new Error('invalidPairingCode'));
        }

        if (expires < Date.now()) {
            return Promise.reject(new Error('pairingCodeExpired'));
        }

        return KayleeUtil.loadControllersByRoomIndex({ roomId: roomId }, false);
    })
    .then(function(obj) {
        if (!obj.controllersByRoom) return Promise.resolve([]);

        var controllerPromises = [];

        for (var controllerId in obj.controllersByRoom) {
            if (!obj.controllersByRoom.hasOwnProperty(controllerId)) continue;
            controllerPromises.push(KayleeUtil.loadControllerData({ controllerId: controllerId }));
        }

        return Promise.all(controllerPromises);
    })
    .then(function(controllerData) {
        for (var i = 0; i < controllerData.length; i++) {
            if (controllerData[i].controllerId === controller.$id) continue;
            if (controllerData[i].controller.appName === controller.appName) {
                return Promise.reject(new Error('duplicateControllerType'));
            }
        }

        return;
    })
    .then(function() {
        return addUpdatesToPairController(
            {}, room.companyId, room.siteId, roomId, controller.$id, userId);
    })
    .then(KayleeBase.update);
}

function requestPairingCode(controllerId) {
    // TODO: consider making this a series of smaller updates to specific parts
    // of the database rather than one large update to the entire database.

    var updates = {};
    var controller;

    return KayleeUtil.loadControllerData({ controllerId: controllerId }, false)
    .then(function(obj) {
        controller = obj.controller;

        if (controller === null) {
            return Promise.reject(new Error('invalidController'));
        }

        // Unpair controller if necessary.

        if (controller.hasOwnProperty('roomId')) {
            return addUpdatesToUnpairController(updates, controller.roomId, controllerId);
        }

        return updates;
    })
    .then(function() { // Load the index of controllers by pairing code.
        return KayleeBase.app.database().ref('index/controllersByPairingCode').once('value');
    })
    .then(function(snap) {
        var codeIndex = snap.exists() ? snap.val() : {};
        var codes = Object.keys(codeIndex);
        var code = generatePairingCode();

        while (codes.includes(code)) { // prevent duplicates
            code = generatePairingCode();
        }

        // If there's already an entry for this controller, delete it.

        for (var key in codeIndex) {
            if (!codeIndex.hasOwnProperty(key)) continue;
            if (codeIndex[key] === controllerId) {
                updates['/index/controllersByPairingCode/' + key] = null;
            }
        }

        updates['/index/controllersByPairingCode/' + code] = controllerId;

        return addUpdatesToSetPairingCode(updates, controllerId, code);
    })
    .then(function() {
        return KayleeBase.update(updates);
    });
}

function Controller(controllerId) {
    this.$nodeType = 'controller';
    FirebaseObject.call(this,
        KayleeBase.app.database().ref('controllers/' + controllerId));
}

Controller.prototype = Object.create(FirebaseObject.prototype);
Controller.prototype.constructor = Controller;

Controller.prototype.$defaults = {
    isCharging: false,
    isInSetup: false,
    language: defaults.inara.language,
    platformVersion: 'unknown',
    settings: {
        appAutoUpdateEnabled: defaults.inara.appAutoUpdateEnabled,
        autoPushErrorLogs: defaults.inara.autoPushErrorLogs,
        factoryInstallMode: false,
        kioskModeEnabled: defaults.inara.kioskModeEnabled,
        otaUpgradeEnabled: defaults.inara.otaUpgradeEnabled,
    },
};

Controller.prototype.$includeInChangedEvent = {
    appVersion: true,
    dbConnected: true,
    platformVersion: true,
};

Controller.prototype.$addUpdatesForChangedEvent =
    KayleeEvent.addUpdatesForControllerChangedEvent;

Controller.prototype.$toJson = function() {
    return FirebaseObject.prototype.$toJson.call(this, [ '$id' ]);
};

Controller.prototype.toString = function() {
    return '[Controller (' + this.$id + ')]';
};

Controller.prototype.$isPaired = function() {
    return this.hasOwnProperty('roomId');
};

Controller.prototype.$requestPairingCode = function() {
    return KayleeBase.request('pairingCodeRequest', {});
};

// Unpair the controller from its room. Returns a promise that resolves when
// the unpairing is complete.

Controller.prototype.$unpair = function() {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    var self = this;
    var roomId = this.roomId;

    if (!this.hasOwnProperty('roomId')) {
        var errorMessage = 'controller is not paired';
        log.error(errorMessage);
        return Promise.reject(new Error(errorMessage));
    }

    return addUpdatesToUnpairController({}, self.roomId, self.$id)
    .then(function(update) {
        return KayleeBase.update(update);
    }).then(function() {
        log.info('unpaired ' + self + ' from room ' + roomId);
        return Promise.resolve();
    }, function(err) {
        log.error('failed unpairing ' + self + ' from room ' + roomId +
            ', error: ' + err);
        return Promise.reject(err);
    });
};

Controller.prototype.$getEvents = function(limit) {
    return KayleeEvents.getEvents(this, limit);
};

Controller.prototype.$getLogs = function(limit) {
    return KayleeLogs.getLogsForController(this, limit);
};

Controller.prototype.$pair = function(room) {
    var self = this;
    var update = {};

    return Promise.resolve()
    .then(function() { // unpair if currently paired
        if (self.$isPaired()) return self.$unpair();
    })
    .then(function() {
        return addUpdatesToPairController(update, room.companyId, room.siteId,
            room.$id, self.$id, KayleeBase.app.auth().currentUser.uid);
    })
    .then(function() {
        return KayleeBase.update(update);
    })
    .catch(function(err) {
        log.error('failed pairing ' + self + ' with ' + room + ', error: ' + err);
        return Promise.reject(err);
    });
};

Controller.prototype.$pushLogs = function(reason, messages) {
    var logId = KayleeBase.app.database().ref().push().key;
    var update = {};

    update['logs/headers/' + logId] = {
        controllerId: this.$id,
        reason: reason,
        time: Date.now(),
    };

    var header = update['logs/headers/' + logId];
    if (this.hasOwnProperty('appName')) header.appName = this.appName;
    if (this.hasOwnProperty('appVersion')) header.appVersion = this.appVersion;
    if (this.hasOwnProperty('appVersionCode')) header.appVersionCode = this.appVersionCode;
    if (this.hasOwnProperty('companyId')) header.companyId = this.companyId;
    if (this.hasOwnProperty('siteId')) header.siteId = this.siteId;
    if (this.hasOwnProperty('roomId')) header.roomId = this.roomId;

    update['logs/messages/' + logId] = messages;
    update['index/logsByController/' + this.$id + '/' + logId] = true;

    var self = this;

    Promise.resolve({})
    .then(function(obj) {
        if (self.hasOwnProperty('roomId')) {
            obj.roomId = self.roomId;
            return KayleeUtil.loadRoomData(obj)
            .then(KayleeUtil.loadSiteData)
            .then(KayleeUtil.loadCompanyData)
            .then(function(obj) {
                header.companyName = obj.company.name;
                header.siteName = obj.site.name;
                header.roomName = obj.room.name;
            });
        }
    }).then(function() {
        // Don't go through KayleeBase.update so we don't log error messages. If we log an error
        // while pushing error logs, that causes us to push another error log, which could cause
        // another error, which causes us to push another error log, ...
        //return KayleeBase.update(update);
        return KayleeBase.app.database().ref().update(update);
    }).catch(function(err) {
        log.warning('failed pushing logs, error: ' + err);
    });
};

Controller.prototype.$requestLogs = function() {
    return this.$ref.child('requests').update({logs: Date.now()});
};

Controller.prototype.$clearLogRequest = function() {
    return this.$ref.child('requests').update({logs: null});
};

Controller.prototype.$requestAppUpdate = function() {
    return this.$ref.child('requests').update({appUpdate: Date.now()});
};

Controller.prototype.$clearAppUpdateRequest = function() {
    return this.$ref.child('requests').update({appUpdate: null});
};

Controller.prototype.$requestNetworkTools = function(action, args) {
    return this.$ref.child('requests/networkTools').push({
        timestamp: Date.now(),
        action: action,
        arguments: args
    });
};

Controller.prototype.$clearNetworkToolsRequests = function() {
    return this.$ref.child('requests').update({networkTools: null});
};

Controller.prototype.$getNetworkToolsResponses = function() {
    var ref = this.$ref.child('responses/networkTools');
    var value;
    return ref.once('value').then(function(snap) {
        value = snap.val();
        if (value === null) return Promise.resolve();
        var updates = {};
        for (var key in value) {
            if (!value.hasOwnProperty(key)) continue;
            updates[key] = null;
        }
        return ref.update(updates);
    }).then(function() {
        return value;
    });
};

Controller.prototype.$clearNetworkToolsResponses = function() {
    return this.$ref.child('responses').update({networkTools: null});
};

Controller.prototype.$requestRestartDevice = function() {
    return this.$ref.child('requests').update({restartDevice: Date.now()});
};

Controller.prototype.$clearRestartDeviceRequest = function() {
    return this.$ref.child('requests').update({restartDevice: null});
};

Controller.prototype.$requestRestartInara = function() {
    return this.$ref.child('requests').update({restartInara: Date.now()});
};

Controller.prototype.$clearRestartInara = function() {
    return this.$ref.child('requests').update({restartInara: null});
};

Controller.prototype.$sendControllerLog = function(data) {
    var log = {
        controllerId: this.$id,
        timestamp: Date.now(),
    };

    [ 'jsHeapSizeLimit', 'totalJsHeapSize', 'usedJsHeapSize',
      'runtimeTotalMemory', 'runtimeMaxMemory', 'runtimeFreeMemory',
      'nativeHeapAllocatedSize', 'nativeHeapFreeSize', 'nativeHeapSize' ]
    .forEach(function(prop) {
        if (prop in data) log[prop] = data[prop];
    });

    return KayleeBase.app.database().ref('controllerLogs').push(log);
};

Controller.prototype.$requestStartActivity = function(className, packageName, flags) {
    var startActivity = {
        className: className,
        packageName: packageName,
    };
    if (typeof flags !== 'undefined') startActivity.flags = flags;
    return this.$ref.child('requests').update({
        startActivity: startActivity
    });
};

module.exports = KayleeController;
